import type { ReactNode } from "react"

import type { RouteObject } from "react-router-dom"

import { isHostedAgentsEnabledInConfig } from "@/hooks/feature-utils"

type RouteIdentity = {
  id?: string
  path?: string
}

const standaloneRouteOverrides = [
  { id: "projects", path: "/projects" },
  { id: "ai-portfolio", path: "/ai-portfolio" },
  { id: "dash", path: "/dash" },
  { id: "workflows", path: "/flows" },
  { id: "documents", path: "/documents" },
] as const

type WorkflowRouteElements = {
  editor: ReactNode
  landing: ReactNode
}

function normalizePath(path: string | undefined): string | undefined {
  if (!path) return undefined
  const withLeadingSlash = path.startsWith("/") ? path : `/${path}`
  return withLeadingSlash.length > 1 ? withLeadingSlash.replace(/\/+$/, "") : withLeadingSlash
}

/**
 * Agents module (HERMES): default the module description to the localized
 * `agents.moduleDescription` key — the useCases/dash pattern in
 * routes-context — unless the backend route carries an admin-configured
 * `info_text`, which keeps winning.
 */
export function applyAgentsModuleDescription<T extends { id?: string; info_text?: unknown }>(
  route: T
): T {
  if (route.id === "agents" && !route.info_text) {
    return { ...route, info_text: "agents.moduleDescription" }
  }
  return route
}

/** Backend id / component of the hosted agents module (builtin_routes_seeder.py). */
const HOSTED_AGENTS_ROUTE_ID = "agents"
const HOSTED_AGENTS_DEFAULT_PATH = "/agents"

function isHostedAgentsRoute(route: { id?: string; component?: string }): boolean {
  return route.id === HOSTED_AGENTS_ROUTE_ID || route.component === HOSTED_AGENTS_ROUTE_ID
}

/**
 * Hosted agents are offered only where the backend says so
 * (`hostedAgentsEnabled`, see `isHostedAgentsEnabledInConfig`). With the
 * switch off the Agents module is dropped from the route list before anything
 * else reads it, so it never reaches the nav, the module gallery, the chat
 * sub-routes or the `/agents/:agentSlug` detail route — even when a stored
 * route config still has it enabled.
 */
export function filterHostedAgentsRoutes<T extends { id?: string; component?: string }>(
  routes: T[],
  hostedAgentsEnabled: boolean
): T[] {
  if (hostedAgentsEnabled) return routes
  return routes.filter((route) => !isHostedAgentsRoute(route))
}

/**
 * With hosted agents switched off, every agents path — the default `/agents`
 * plus whatever path a stored route config gave the module — and everything
 * under it (bookmarks to a thread, the M365 connection page's `/agents`
 * return) redirects home instead of landing on the not-found page. Empty
 * when hosted agents are on: the module owns those paths then.
 */
export function buildHostedAgentsDisabledRedirects(
  configuredRoutes: { id?: string; component?: string; path?: string }[],
  hostedAgentsEnabled: boolean,
  homeElement: ReactNode
): RouteObject[] {
  if (hostedAgentsEnabled) return []
  const paths = new Set<string>([HOSTED_AGENTS_DEFAULT_PATH])
  for (const route of configuredRoutes) {
    const path = normalizePath(route.path)
    if (isHostedAgentsRoute(route) && path && path !== "/") paths.add(path)
  }
  return [...paths].map((path) => ({ path: `${path}/*`, element: homeElement }))
}

/**
 * The hosted agents switch applied to the backend route list in one step: the
 * routes to build the app from, plus the redirects that send old agents links
 * home while the switch is off (see the two helpers above).
 */
export function gateHostedAgentsRoutes<
  T extends { id?: string; component?: string; path?: string },
>(
  config: { hostedAgentsEnabled?: unknown; frontend: { routes: T[] } },
  homeElement: ReactNode
): { routes: T[]; hostedAgentsRedirects: RouteObject[] } {
  const enabled = isHostedAgentsEnabledInConfig(config)
  const { routes } = config.frontend
  return {
    routes: filterHostedAgentsRoutes(routes, enabled),
    hostedAgentsRedirects: buildHostedAgentsDisabledRedirects(routes, enabled, homeElement),
  }
}

export function isStandaloneRouteOverride(route: RouteIdentity): boolean {
  const routePath = normalizePath(route.path)

  return standaloneRouteOverrides.some(
    (standaloneRoute) => route.id === standaloneRoute.id || routePath === standaloneRoute.path
  )
}

/**
 * Hosted-agent detail sub-route (HERMES): `<agents path>/:agentSlug` — thread
 * list + chat view. Gated on the agents module being enabled, like the other
 * module sub-routes. The static "chat" segment of the auto-generated
 * `<agents path>/chat/:sessionId` route outranks `:agentSlug`, so both coexist.
 *
 * The detail route REUSES the module's own element (the `agents` registry
 * component switches on route params internally). Sharing one element across
 * `/agents`, `/agents/:agentSlug` and `/agents/chat/:sessionId` keeps the
 * component type identical at the same tree position, so React updates rather
 * than remounts on navigation — exactly how the base + `/chat/:sessionId`
 * routes already share `route.element`. An in-flight streamed answer survives
 * the create-on-send navigation because of this.
 */
export function buildAgentsDetailRouteObjects(
  agentsRoute: { path: string; enabled: boolean; element?: ReactNode } | null | undefined
): RouteObject[] {
  if (!agentsRoute?.enabled || !agentsRoute.element) return []
  return [
    {
      path: `${agentsRoute.path}/:agentSlug`,
      element: agentsRoute.element,
    },
  ]
}

export function buildWorkflowRouteObjects({
  editor,
  landing,
}: WorkflowRouteElements): RouteObject[] {
  return [
    {
      path: "flows",
      children: [
        {
          path: "new",
          element: editor,
        },
        {
          path: ":id",
          element: editor,
        },
        {
          index: true,
          element: landing,
        },
      ],
    },
  ]
}

export function prependRouteObjects(
  priorityRoutes: RouteObject[],
  routes: RouteObject[]
): RouteObject[] {
  return [...priorityRoutes, ...routes]
}
